import ObexRequestsHandlerP80 from '../handlers/ObexRequestsHandlerP80';
import {
  FixedAsset,
  FixedAssetPayload,
  FixedAssetValuation,
  FixedAssetValuationPayload,
  DepreciationEntry,
  ValuationArea,
  AccountingSchedule
} from '../types/FixedAsset.types';
import { getAccessToken, buildQuery, unwrapList, unwrapRecord, unwrapError } from './apiUtils';

const ROUTES = {
  list:            '/fixed_assets_list',
  get:             '/fixed_assets_get',
  create:          '/fixed_assets_create',
  update:          '/fixed_assets_update',
  valuationsList:  '/fixed_asset_valuations_list',
  valuationCreate: '/fixed_asset_valuations_create',
  entriesList:     '/fixed_asset_depreciation_entries_list',
  entryPost:       '/fixed_asset_depreciation_entries_post'
};

const asId = (value: any): string =>
  value !== undefined && value !== null ? String(value) : '';

const asNumber = (value: any): number => {
  const n = Number(value);
  return isNaN(n) ? 0 : n;
};

export const mapFixedAsset = (raw: any): FixedAsset => ({
  id:               asId(raw.id),
  companyId:        asId(raw.company_id),
  companyName:      raw.company_name || '',
  code:             raw.code || '',
  name:             raw.name || '',
  supplierId:       asId(raw.supplier_id),
  supplierName:     raw.supplier_name || '',
  costCenterId:     asId(raw.cost_center_id),
  costCenterName:   raw.cost_center_name || '',
  assetAccountId:   asId(raw.asset_account_id),
  assetAccountName: raw.asset_account_name || '',
  acquisitionDate:  raw.acquisition_date || '',
  status:           raw.status === 'retired' ? 'retired' : raw.status === 'disposed' ? 'disposed' : 'active'
});

const asValuationArea = (value: any): ValuationArea =>
  value === 'fiscal' ? 'fiscal' : value === 'derived' ? 'derived' : 'accounting';

export const mapValuation = (raw: any): FixedAssetValuation => ({
  id:                      asId(raw.id),
  fixedAssetId:            asId(raw.fixed_asset_id),
  fixedAssetName:          raw.fixed_asset_name || '',
  valuationArea:           asValuationArea(raw.valuation_area),
  acquisitionValue:        asNumber(raw.acquisition_value),
  depreciationMethodId:    asId(raw.depreciation_method_id),
  depreciationMethodCode:  raw.depreciation_method_code || '',
  depreciationMethodName:  raw.depreciation_method_name || '',
  usefulLifeMonths:        asNumber(raw.useful_life_months),
  residualValue:           asNumber(raw.residual_value),
  depreciationStartDate:   raw.depreciation_start_date || '',
  accumulatedDepreciation: asNumber(raw.accumulated_depreciation)
});

export const mapDepreciationEntry = (raw: any): DepreciationEntry => ({
  id:                     asId(raw.id),
  fixedAssetValuationId:  asId(raw.fixed_asset_valuation_id),
  fiscalYear:             asNumber(raw.fiscal_year),
  period:                 asNumber(raw.period),
  movementType:           raw.movement_type || 'normal_depreciation',
  plannedAmount:          asNumber(raw.planned_amount),
  status:                 raw.status === 'posted' ? 'posted' : 'planned',
  postedAt:               raw.posted_at || '',
  journalEntryId:         asId(raw.journal_entry_id)
});

// OJO: /fixed_assets_list no admite filtro por supplier_id ni por cost_center_id
// (solo company_id, status y search). Esos filtrados se hacen en cliente.
export const listFixedAssets = async (
  options: { companyId?: string; status?: string; search?: string; page?: number; pageSize?: number } = {}
): Promise<FixedAsset[]> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const query = buildQuery({
    access_token: token,
    company_id:   options.companyId,
    status:       options.status,
    search:       options.search,
    page:         options.page || 1,
    page_size:    options.pageSize || 200
  });

  const res = await ObexRequestsHandlerP80.get(`${ROUTES.list}${query}`);
  return unwrapList(res).map(mapFixedAsset);
};

export const getFixedAsset = async (id: string): Promise<FixedAsset | null> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const query = buildQuery({ access_token: token, id });
  const res = await ObexRequestsHandlerP80.get(`${ROUTES.get}${query}`);
  const record = unwrapRecord(res);
  return record ? mapFixedAsset(record) : null;
};

export const createFixedAsset = async (payload: FixedAssetPayload): Promise<FixedAsset> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const query = buildQuery({
    access_token:     token,
    company_id:       payload.companyId,
    code:             payload.code,
    name:             payload.name,
    supplier_id:      payload.supplierId,
    cost_center_id:   payload.costCenterId,
    asset_account_id: payload.assetAccountId,
    acquisition_date: payload.acquisitionDate,
    status:           payload.status || 'active'
  });

  const res = await ObexRequestsHandlerP80.get(`${ROUTES.create}${query}`);
  const record = unwrapRecord(res);
  if (!record) throw new Error(unwrapError(res, 'Error al dar de alta el activo fijo.'));
  return mapFixedAsset(record);
};

// fixed_assets/update.avap no acepta ni company_id ni code: son inmutables.
export const updateFixedAsset = async (id: string, payload: Partial<FixedAssetPayload>): Promise<FixedAsset> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const query = buildQuery({
    access_token:     token,
    id,
    name:             payload.name,
    supplier_id:      payload.supplierId,
    cost_center_id:   payload.costCenterId,
    asset_account_id: payload.assetAccountId,
    acquisition_date: payload.acquisitionDate,
    status:           payload.status
  });

  const res = await ObexRequestsHandlerP80.get(`${ROUTES.update}${query}`);
  const record = unwrapRecord(res);
  if (!record) throw new Error(unwrapError(res, 'Error al modificar el activo fijo.'));
  return mapFixedAsset(record);
};

export const listValuations = async (fixedAssetId: string): Promise<FixedAssetValuation[]> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const query = buildQuery({ access_token: token, fixed_asset_id: fixedAssetId });
  const res = await ObexRequestsHandlerP80.get(`${ROUTES.valuationsList}${query}`);
  return unwrapList(res).map(mapValuation);
};

export const createValuation = async (payload: FixedAssetValuationPayload): Promise<FixedAssetValuation> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const query = buildQuery({
    access_token:             token,
    fixed_asset_id:           payload.fixedAssetId,
    valuation_area:           payload.valuationArea,
    acquisition_value:        payload.acquisitionValue,
    depreciation_method_id:   payload.depreciationMethodId,
    useful_life_months:       payload.usefulLifeMonths,
    residual_value:           payload.residualValue,
    depreciation_start_date:  payload.depreciationStartDate,
    accumulated_depreciation: payload.accumulatedDepreciation !== undefined ? payload.accumulatedDepreciation : 0
  });

  const res = await ObexRequestsHandlerP80.get(`${ROUTES.valuationCreate}${query}`);
  const record = unwrapRecord(res);
  if (!record) throw new Error(unwrapError(res, 'Error al crear la valoración.'));
  return mapValuation(record);
};

export const listDepreciationEntries = async (
  valuationId: string,
  options: { fiscalYear?: number; status?: string } = {}
): Promise<DepreciationEntry[]> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const query = buildQuery({
    access_token:             token,
    fixed_asset_valuation_id: valuationId,
    fiscal_year:              options.fiscalYear,
    status:                   options.status
  });

  const res = await ObexRequestsHandlerP80.get(`${ROUTES.entriesList}${query}`);
  return unwrapList(res).map(mapDepreciationEntry);
};

// Valoraciones y cuotas solo admiten un activo por llamada: van en paralelo
export const listAccountingSchedules = async (
  options: { companyId?: string; status?: string } = {}
): Promise<AccountingSchedule[]> => {
  const assets = await listFixedAssets(options);
  return Promise.all(assets.map(async (asset): Promise<AccountingSchedule> => {
    const valuation = (await listValuations(asset.id)).filter(v => v.valuationArea === 'accounting')[0] || null;
    return { asset, valuation, entries: valuation ? await listDepreciationEntries(valuation.id) : [] };
  }));
};

/**
 * Marca una cuota como contabilizada. `journalEntryId` es opcional: hoy no hay
 * trigger que genere el asiento, así que se envía solo si el llamante lo tiene.
 */
export const postDepreciationEntry = async (
  id: string,
  journalEntryId?: string
): Promise<DepreciationEntry> => {
  const token = getAccessToken();
  if (!token) throw new Error('Sesión no válida o expirada.');

  const query = buildQuery({
    access_token:     token,
    id,
    journal_entry_id: journalEntryId
  });

  const res = await ObexRequestsHandlerP80.get(`${ROUTES.entryPost}${query}`);
  const record = unwrapRecord(res);
  if (!record) throw new Error(unwrapError(res, 'Error al contabilizar la cuota.'));
  return mapDepreciationEntry(record);
};
